import { Client, clientSingleton } from '@edge/spark/io-apollo'
import { getIsAuthenticated, useIsAuthenticated } from '@edge/spark/io-aws'
import { filterMaybe } from '@edge/spark/utils'
import { AllOrganizationsDocument, AllOrganizationsQuery, useAllOrganizationsQuery } from '../generated/graphql'
import { validateAuthorizedAuctions } from '../utils'

type UseAllOrganizationsProps = Parameters<typeof useAllOrganizationsQuery>[0] & {
    /** Validate results before returning. Defaults to true */
    validate?: boolean
}

/** Uses Apollo Context through `useAllOrganizationsQuery` */
export const useAllOrganizations = (props?: UseAllOrganizationsProps) => {
    const isAuthenticated = useIsAuthenticated()
    const { validate = true, skip: propsSkip, ...opts } = props || {}
    const skip = propsSkip || !isAuthenticated
    const { data, loading } = useAllOrganizationsQuery({ fetchPolicy: 'cache-first', skip, ...opts })

    if (skip) {
        isAuthenticated
            ? console.debug('useAllOrganizations: Skip')
            : console.debug('useAllOrganizations: Not authenticated')
        return null
    }

    const orgs = data?.user?.organizations?.filter(filterMaybe)

    // Loading and no cached data
    if (loading && orgs?.length === 0) {
        return null
    }

    if (!orgs?.length) {
        return []
    }

    if (!validate) {
        return orgs
    }

    const isValid = validateAuthorizedAuctions(orgs)

    return isValid ? orgs : null
}

interface GetAllOrganizationsProps {
    /** ApolloClient. Attempts to use client singleton if not passed */
    client?: Client
    /** Validate results before returning. Defaults to true */
    validate?: boolean
}

/** Uses passed client or clientSingleton for `AllOrganizationsQuery` */
export const getAllOrganizations = async (props?: GetAllOrganizationsProps) => {
    const isAuthenticated = await getIsAuthenticated()

    if (!isAuthenticated) {
        console.debug('getAllOrganizations: Not authenticated')
        return null
    }

    const { client: propsClient, validate = true } = props || {}
    const client = propsClient || clientSingleton
    const { data } = await client.query<AllOrganizationsQuery>({
        query: AllOrganizationsDocument,
        fetchPolicy: 'cache-first',
    })
    const orgs = data?.user?.organizations?.filter(filterMaybe) || []

    if (!validate) {
        return orgs
    }

    const isValid = validateAuthorizedAuctions(orgs)

    return isValid ? orgs : null
}
